import { useSnackbar } from 'baseui/snackbar';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import {
  AlertActions,
  AlertDescription,
  AlertDialog,
  AlertTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';
import { isMutationSuccess } from 'services/api/base-api';
import { useDeleteChecklistMutation } from 'services/api/checklists/endpoints';
import { useLocale } from 'utils/hooks/useLocale';

export default function DeleteChecklist() {
  const { t } = useLocale();
  const { checklistId } = useParams();
  const id = z.coerce.number().catch(0).parse(checklistId);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const [deleteChecklist] = useDeleteChecklistMutation();
  const form = useForm<{ id: number }>({ defaultValues: { id } });

  const cancel = () =>
    navigate(
      { pathname: '..', search: `?${searchParams.toString()}` },
      { replace: true },
    );

  const onSubmit = form.handleSubmit(async () => {
    const result = await deleteChecklist(id);
    if (isMutationSuccess(result)) {
      snackbar.enqueue({ message: t('Checklist deleted successfully') });
      navigate({ pathname: '..', search: '' }, { replace: true });
    } else {
      snackbar.enqueue({ message: t('Failed to delete checklist') });
    }
  });

  return (
    <AlertDialog isOpen>
      <AlertTitle>{t('Delete Checklist')}</AlertTitle>
      <AlertDescription>
        {t(
          'Are you sure you want to delete this checklist? Existing work order copies will not be affected',
        )}
      </AlertDescription>
      <AlertActions>
        <Button
          variant="plain"
          isDisabled={form.formState.isSubmitting}
          onClick={cancel}
        >
          {t('Cancel')}
        </Button>
        <form onSubmit={onSubmit}>
          <Button
            color="red"
            type="submit"
            isLoading={form.formState.isSubmitting}
          >
            {t('Delete Checklist')}
          </Button>
        </form>
      </AlertActions>
    </AlertDialog>
  );
}
